<template>
    <v-dialog :model-value="modelValue" max-width="760" scrollable @update:model-value="$emit('update:modelValue', $event)">
        <v-card>
            <v-card-title class="text-subtitle-1 d-flex align-center py-3 px-4">
                CC 規則設定
                <v-spacer />
                <v-btn size="x-small" variant="outlined" @click="onResetDefaults">重置為預設</v-btn>
            </v-card-title>
            <v-divider />

            <!-- 主體：左側職業清單 + 右側設定 -->
            <v-card-text class="pa-0" style="display: flex; max-height: 62vh; min-height: 360px;">

                <!-- 左側職業清單 -->
                <div class="job-sidebar">
                    <v-list density="compact" nav class="pa-1">
                        <v-list-item
                            v-for="cfg in localConfigs"
                            :key="cfg.id"
                            :active="settingsJobTab === cfg.id"
                            active-color="primary"
                            rounded="sm"
                            class="px-2 py-1"
                            @click="settingsJobTab = cfg.id"
                        >
                            <v-list-item-title class="text-body-2">{{ cfg.name }}</v-list-item-title>
                        </v-list-item>
                    </v-list>
                </div>

                <!-- 右側設定區域 -->
                <div class="job-detail pa-3">
                    <template v-if="activeConfig">
                        <div class="text-caption text-disabled mb-3">
                            偵測技能：{{ activeConfig.detectionSkillIds.join(', ') }}
                        </div>

                        <div v-if="activeConfig.rules.length === 0" class="text-body-2 text-disabled text-center py-4">
                            此職業未設定任何規則
                        </div>

                        <!-- 規則卡片 -->
                        <div
                            v-for="rule in activeConfig.rules"
                            :key="rule.ruleId"
                            class="rule-card pa-3 mb-3 rounded"
                        >
                            <div class="d-flex align-center mb-3" style="gap: 8px;">
                                <v-text-field
                                    v-if="editData[rule.ruleId]"
                                    v-model="editData[rule.ruleId].label"
                                    label="規則名稱"
                                    density="compact"
                                    hide-details
                                    variant="outlined"
                                    class="flex-grow-1"
                                />
                                <v-tooltip text="顯示為結果表格的欄位" location="top">
                                    <template #activator="{ props: tip }">
                                        <v-btn
                                            v-if="editData[rule.ruleId]"
                                            v-bind="tip"
                                            :color="editData[rule.ruleId].showInTable ? 'primary' : undefined"
                                            :variant="editData[rule.ruleId].showInTable ? 'tonal' : 'outlined'"
                                            size="small"
                                            @click="editData[rule.ruleId].showInTable = !editData[rule.ruleId].showInTable"
                                        >
                                            <v-icon size="small">mdi-table-column</v-icon>
                                            <span class="ml-1 text-caption">{{ editData[rule.ruleId].showInTable ? '顯示' : '隱藏' }}</span>
                                        </v-btn>
                                    </template>
                                </v-tooltip>
                                <v-btn icon size="small" variant="text" color="error" @click="deleteRule(activeConfig.id, rule.ruleId)">
                                    <v-icon size="small">mdi-delete-outline</v-icon>
                                </v-btn>
                            </div>

                            <template v-if="editData[rule.ruleId]">
                                <v-autocomplete
                                    v-model="editData[rule.ruleId].skillIds"
                                    :items="skillSelectItems"
                                    item-title="title"
                                    item-value="value"
                                    multiple chips closable-chips clearable
                                    label="技能（空 = 全部非被動技能）"
                                    density="compact" hide-details variant="outlined" class="mb-3"
                                />
                                <v-row dense>
                                    <v-col cols="6">
                                        <div class="d-flex align-center mb-1" style="gap: 6px;">
                                            <span class="text-caption text-medium-emphasis">自身 Buff CC</span>
                                            <v-btn-toggle
                                                v-model="editData[rule.ruleId].selfCCMode"
                                                density="compact" variant="outlined" divided
                                                style="height: 22px;"
                                            >
                                                <v-btn value="and" size="x-small" style="min-width: 36px; height: 22px; font-size: 10px;">AND</v-btn>
                                                <v-btn value="or"  size="x-small" style="min-width: 36px; height: 22px; font-size: 10px;">OR</v-btn>
                                            </v-btn-toggle>
                                        </div>
                                        <v-autocomplete
                                            v-model="editData[rule.ruleId].selfCCIds"
                                            :items="ccSelectItems"
                                            item-title="title" item-value="value"
                                            multiple chips closable-chips clearable
                                            density="compact" hide-details variant="outlined"
                                        />
                                    </v-col>
                                    <v-col cols="6">
                                        <div class="d-flex align-center mb-1" style="gap: 6px;">
                                            <span class="text-caption text-medium-emphasis">目標 Debuff CC</span>
                                            <v-btn-toggle
                                                v-model="editData[rule.ruleId].targetCCMode"
                                                density="compact" variant="outlined" divided
                                                style="height: 22px;"
                                            >
                                                <v-btn value="and" size="x-small" style="min-width: 36px; height: 22px; font-size: 10px;">AND</v-btn>
                                                <v-btn value="or"  size="x-small" style="min-width: 36px; height: 22px; font-size: 10px;">OR</v-btn>
                                            </v-btn-toggle>
                                        </div>
                                        <v-autocomplete
                                            v-model="editData[rule.ruleId].targetCCIds"
                                            :items="ccSelectItems"
                                            item-title="title" item-value="value"
                                            multiple chips closable-chips clearable
                                            density="compact" hide-details variant="outlined"
                                        />
                                    </v-col>
                                </v-row>
                            </template>
                        </div>

                        <v-btn size="small" variant="outlined" prepend-icon="mdi-plus" @click="addRule(activeConfig.id)">
                            新增規則
                        </v-btn>
                    </template>
                </div>

            </v-card-text>

            <v-divider />
            <v-card-actions class="px-4 py-2">
                <span class="text-caption text-disabled">AND = 全部同時存在；OR = 任一存在即成立</span>
                <v-spacer />
                <v-btn variant="text" @click="$emit('update:modelValue', false)">取消</v-btn>
                <v-btn color="primary" variant="tonal" @click="saveSettings">儲存</v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script lang="ts">
import { defineComponent, computed, ref, watch } from "vue";
import { condNameMap, skillNameMap } from "@/store";
import {
    jobCCConfigs,
    saveAndSyncJobCCConfigs,
    deepCloneConfigs,
    resetToDefaultConfigs,
    type JobCCConfig,
} from "@/skillCCConfig";

type EditEntry = {
    label: string;
    skillIds: number[];
    selfCCIds: number[];
    selfCCMode: "and" | "or";
    targetCCIds: number[];
    targetCCMode: "and" | "or";
    showInTable: boolean;
};

export default defineComponent({
    name: "JobCCConfigDialog",
    props: {
        modelValue: { type: Boolean, default: false },
    },
    emits: ["update:modelValue"],
    setup(props, { emit }) {
        const settingsJobTab = ref("");
        const localConfigs   = ref<JobCCConfig[]>([]);
        const editData       = ref<Record<string, EditEntry>>({});

        const activeConfig = computed(() =>
            localConfigs.value.find((c) => c.id === settingsJobTab.value) ?? null,
        );

        watch(
            () => props.modelValue,
            (open) => {
                if (!open) return;
                localConfigs.value = deepCloneConfigs(jobCCConfigs.value);
                if (!settingsJobTab.value || !localConfigs.value.find(c => c.id === settingsJobTab.value))
                    settingsJobTab.value = localConfigs.value[0]?.id ?? "";
                buildEditData();
            },
            { immediate: true },
        );

        function buildEditData() {
            const map: Record<string, EditEntry> = {};
            for (const cfg of localConfigs.value) {
                for (const rule of cfg.rules) {
                    map[rule.ruleId] = {
                        label:        rule.label,
                        skillIds:     [...rule.skillIds],
                        selfCCIds:    [...rule.selfCCIds],
                        selfCCMode:   rule.selfCCMode ?? "and",
                        targetCCIds:  [...rule.targetCCIds],
                        targetCCMode: rule.targetCCMode ?? "and",
                        showInTable:  rule.showInTable !== false,
                    };
                }
            }
            editData.value = map;
        }

        function applyEditData() {
            for (const cfg of localConfigs.value) {
                for (const rule of cfg.rules) {
                    const e = editData.value[rule.ruleId];
                    if (!e) continue;
                    rule.label        = e.label.trim() || rule.label;
                    rule.skillIds     = [...e.skillIds];
                    rule.selfCCIds    = [...e.selfCCIds];
                    rule.selfCCMode   = e.selfCCMode;
                    rule.targetCCIds  = [...e.targetCCIds];
                    rule.targetCCMode = e.targetCCMode;
                    rule.showInTable  = e.showInTable;
                }
            }
        }

        function addRule(jobId: string) {
            const job = localConfigs.value.find((c) => c.id === jobId);
            if (!job) return;
            const ruleId = `${jobId}-${Date.now()}`;
            job.rules.push({ ruleId, label: "新規則", skillIds: [], selfCCIds: [], selfCCMode: "and", targetCCIds: [], targetCCMode: "and", showInTable: true });
            editData.value = {
                ...editData.value,
                [ruleId]: { label: "新規則", skillIds: [], selfCCIds: [], selfCCMode: "and", targetCCIds: [], targetCCMode: "and", showInTable: true },
            };
        }

        function deleteRule(jobId: string, ruleId: string) {
            const job = localConfigs.value.find((c) => c.id === jobId);
            if (!job) return;
            job.rules = job.rules.filter((r) => r.ruleId !== ruleId);
            const next = { ...editData.value };
            delete next[ruleId];
            editData.value = next;
        }

        function saveSettings() {
            applyEditData();
            saveAndSyncJobCCConfigs(localConfigs.value);
            emit("update:modelValue", false);
        }

        function onResetDefaults() {
            localConfigs.value = resetToDefaultConfigs();
            buildEditData();
        }

        const ccSelectItems = computed(() =>
            Object.entries(condNameMap.value)
                .map(([id, name]) => ({ title: `${name}（${id}）`, value: Number(id) }))
                .sort((a, b) => a.value - b.value),
        );
        const skillSelectItems = computed(() =>
            Object.entries(skillNameMap.value)
                .map(([id, name]) => ({ title: `${name}（${id}）`, value: Number(id) }))
                .sort((a, b) => a.value - b.value),
        );

        return {
            settingsJobTab, localConfigs, editData, activeConfig,
            ccSelectItems, skillSelectItems,
            addRule, deleteRule, saveSettings, onResetDefaults,
        };
    },
});
</script>

<style scoped>
.rule-card {
    background: rgba(255, 255, 255, 0.04);
    border: 1px solid rgba(255, 255, 255, 0.08);
}
.job-sidebar {
    width: 96px;
    flex-shrink: 0;
    border-right: 1px solid rgba(128, 128, 128, 0.15);
    overflow-y: auto;
}
.job-detail {
    flex: 1;
    overflow-y: auto;
    min-width: 0;
}
</style>
